import React, { useEffect, useRef, useState, useCallback } from "react";
import { X, ArrowUpRight } from "lucide-react";
import { fetchCommunityResults, handleTeamClick } from "@/lib/tracking";

// Refresh the Drive list every 5 minutes so newly uploaded images appear
// automatically without a redeploy (backend also caches for ~5 min).
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

// Card visible size — identical for every image regardless of source dimensions.
// Portrait-ish card to fit trading screenshots without heavy cropping.
const CARD_W = 260; // px
const CARD_H = 340; // px

// Marquee speed: ~50px per second. Duration depends on total track width.
const PX_PER_SEC = 50;

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

/* ---------- Watermark ---------- */
const Watermark = ({ className = "" }) => (
  <span
    aria-hidden
    data-testid="community-watermark"
    className={`pointer-events-none select-none absolute bottom-2 right-2 px-2 py-0.5 rounded-md text-[9.5px] font-bold uppercase tracking-[0.16em] text-white/85 bg-black/45 backdrop-blur-[2px] border border-white/10 shadow-sm ${className}`}
  >
    Trading Room
  </span>
);

/* ---------- Lightbox ---------- */
const Lightbox = ({ src, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      data-testid="community-lightbox"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 sm:p-8 animate-fade-up"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" />
      <div
        className="relative max-w-[95vw] max-h-[92vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative inline-block">
          <img
            src={src}
            alt="Rezultat comunitate"
            className="max-w-[95vw] max-h-[92vh] w-auto h-auto object-contain rounded-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
          />
          <Watermark className="text-[11px] px-2.5 py-1 rounded-lg bottom-3 right-3" />
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Închide"
          data-testid="community-lightbox-close"
          className="absolute -top-3 -right-3 sm:top-3 sm:right-3 w-10 h-10 rounded-full bg-white text-black hover:bg-neutral-100 shadow-lg flex items-center justify-center transition"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

/* ---------- Card ---------- */
const ResultCard = ({ item, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(item)}
    data-testid={`community-card-${item.id}`}
    className="relative shrink-0 rounded-2xl overflow-hidden border border-black/10 bg-neutral-100 shadow-[0_8px_20px_-12px_rgba(17,24,39,0.18)] hover:shadow-[0_14px_28px_-14px_rgba(17,24,39,0.28)] hover:-translate-y-[2px] transition-all duration-300"
    style={{ width: CARD_W, height: CARD_H }}
    aria-label="Vezi rezultat comunitate"
  >
    <img
      src={item.thumb_url}
      alt="Rezultat comunitate Trading Room"
      loading="lazy"
      decoding="async"
      className="w-full h-full object-contain bg-neutral-100"
      draggable={false}
    />
    <Watermark />
  </button>
);

/* ---------- Section ---------- */
export default function CommunityResults() {
  const [items, setItems] = useState(null); // null = loading, [] = failed/empty
  const [openItem, setOpenItem] = useState(null);
  const reduced = useReducedMotion();
  const wrapperRef = useRef(null);

  // Fetch + auto-refresh
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetchCommunityResults()
        .then((list) => {
          if (cancelled) return;
          setItems(list || []);
        })
        .catch(() => {
          if (!cancelled) setItems([]);
        });
    };
    load();
    const id = setInterval(load, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const handleOpen = useCallback((it) => setOpenItem(it), []);
  const handleClose = useCallback(() => setOpenItem(null), []);

  // Graceful failure: hide the whole section when no valid items are available.
  if (!items || items.length === 0) return null;

  // Duplicate list for seamless marquee loop (translate -50% lands exactly at
  // start of the second copy).
  const trackItems = [...items, ...items];

  // Base gap between cards.
  const GAP = 12; // px
  const singleListWidth = items.length * (CARD_W + GAP);
  const durationSec = Math.max(20, Math.round(singleListWidth / PX_PER_SEC));

  return (
    <section
      data-testid="community-results-section"
      className="px-5 mt-12 sm:mt-14"
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center sm:text-left max-w-md mx-auto sm:mx-0">
          <h2 className="font-display text-lg sm:text-xl font-extrabold text-black tracking-tight">
            Rezultate din comunitate <span aria-hidden>📈</span>
          </h2>
          <p className="mt-1.5 text-[13px] text-black/55 leading-relaxed">
            Rezultate împărtășite de membrii comunității Trading Room
          </p>
        </div>

        {reduced ? (
          // Reduced-motion fallback — manual horizontal scroll, no animation.
          <div
            data-testid="community-results-static"
            className="mt-5 flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-1"
            style={{ scrollbarWidth: "none" }}
          >
            {items.map((it) => (
              <div key={it.id} className="snap-start">
                <ResultCard item={it} onOpen={handleOpen} />
              </div>
            ))}
          </div>
        ) : (
          <div
            ref={wrapperRef}
            data-testid="community-results-marquee-wrapper"
            data-paused={openItem ? "true" : "false"}
            className="community-marquee-wrapper mt-5 overflow-hidden relative"
            style={{
              // Soft fade at both edges so items don't hard-cut.
              maskImage:
                "linear-gradient(to right, transparent 0, black 40px, black calc(100% - 40px), transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(to right, transparent 0, black 40px, black calc(100% - 40px), transparent 100%)",
            }}
          >
            <div
              className="community-marquee flex"
              style={{
                gap: `${GAP}px`,
                width: "max-content",
                ["--community-marquee-duration"]: `${durationSec}s`,
              }}
            >
              {trackItems.map((it, idx) => (
                <ResultCard
                  key={`${it.id}-${idx}`}
                  item={it}
                  onOpen={handleOpen}
                />
              ))}
            </div>
          </div>
        )}

        {/* Discrete CTA — reuses handleTeamClick so GA4 & OpenAI Pixel
            attribution stay consistent (source="community_cta" for GA
            report segmentation). */}
        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={() => handleTeamClick("community_cta")}
            data-testid="community-cta-team"
            className="group inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-black border border-black/15 hover:border-black/40 shadow-[0_4px_14px_-6px_rgba(17,24,39,0.15)] hover:shadow-[0_8px_18px_-8px_rgba(17,24,39,0.25)] transition-all duration-300"
          >
            <span className="text-[12.5px] sm:text-[13px] font-semibold tracking-tight">
              Vreau și eu rezultate ca acestea
            </span>
            <ArrowUpRight className="w-3.5 h-3.5 text-[#C9A24A] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>
        </div>
      </div>

      {openItem && <Lightbox src={openItem.full_url} onClose={handleClose} />}
    </section>
  );
}
